import { FunctionComponent } from 'react';
import { Trans } from 'react-i18next';

import useElasticSearch from '../../hooks/useElasticSearch';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import ExinSelect from '../styled/ExinSelect';
import { Certification } from './StepCertification';
import { Method, methodMap } from './StepCertificationMethod';

export type Language = {
  value: string;
  label: string;
};

/**
 * Takes a raw server response and transforms it into the data that our components prefer.
 */
function transformServerResponse(garbage: unknown): Array<Language> {
  return (garbage as any).aggregations.training_languages.inner.language_name.buckets.map(
    (bucket: any) => ({
      value: bucket.key,
      label: bucket.key,
    })
  );
}

const StepTrainingLanguage: FunctionComponent<{
  certification: Certification;
  method: Method.ELEARNING_EXAM | Method.TRAINING_EXAM | Method.VIRTUAL_CLASSROOM;
  selectedLanguage: Language | null;
  setSelectedLanguage(value: Language | null): void;
}> = ({ certification, method, selectedLanguage, setSelectedLanguage }) => {
  const { isLoading, response: languages } = useElasticSearch(
    {
      query: {
        term: {
          'post_type.raw': 'ex_partner',
        },
      },
      size: 0,
      aggs: {
        training_languages: {
          nested: {
            path: 'accreditations',
          },
          aggs: {
            inner: {
              filter: {
                bool: {
                  must: [
                    {
                      term: {
                        'accreditations.certification_id': certification.value,
                      },
                    },
                    {
                      term: {
                        'accreditations.training_method_id': methodMap[method],
                      },
                    },
                  ],
                },
              },
              aggs: {
                language_name: {
                  terms: {
                    field: 'accreditations.training_language_name',
                    order: {
                      _key: 'asc',
                    },
                    size: 100,
                  },
                },
              },
            },
          },
        },
      },
    },
    transformServerResponse
  );

  if (isLoading) {
    return <ExinMuiCircularProgress />;
  }

  if (!languages?.length) {
    return <Trans i18nKey="messageNoCertificationLanguages">No languages found</Trans>;
  }

  return (
    <ExinSelect
      options={languages}
      value={selectedLanguage}
      // @ts-ignore
      // Something in react-select#Select demands onChange data to be `unknown`
      // TypeScript will complain, because our own data is in fact _not_ unknown.
      onChange={setSelectedLanguage}
      placeholder={<Trans i18nKey="placeholderSelect">Select</Trans>}
    />
  );
};

export default StepTrainingLanguage;
